import { assetUrl } from "@/utils/cdn"
import useAuth from "@/utils/client-auth"
import { isStringUrl } from "@/utils/functions"
import { NotificationContext } from "@/utils/notification"
import axios, { AxiosError } from "axios"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import { StartSessionResponse } from "pages/api/wizard-start-session"
import { useContext, useEffect, useState } from "react"
import { z } from "zod"
import { AnimatedWords } from "../animated-words"
import Bubble from "../bubble"
import { CustomThemedResource } from "../image"
import { ErrorMessage } from "../message"
import { SubscriptionPopup } from "../subscription-popup"
import { PlatformsSection } from "./headline"
import { GetHeader as GetAnimatedHeader } from "./headline-alternative"

const tagSchema = z.enum(["h1", "h2", "h3", "h4"])

const titleAttrSchema = z
  .array(
    z.object({
      start: z.number(),
      length: z.number(),
      color: z.string().optional(),
      dark_color: z.string().optional(),
    }),
  )
  .optional()

export const textareaSchema = z.object({
  component: z.literal("headline-textarea"),
  bubble_text: z.string().optional(),
  title: z.string(),
  subtitle: z.string(),
  button_text: z.string(),
  without_background: z.boolean().optional(),
  reversed: z.boolean().optional(),
  without_qr: z.boolean().optional(),
  tag: tagSchema.optional().default("h1"),
  title_attr: titleAttrSchema,
  left_icon: z.string().optional(),
  right_icon: z.string().optional(),
  className: z.string().optional(),
  placeholder: z.string(),
  words: z
    .object({
      name: z.string(),
      lightColor: z.string(),
      darkColor: z.string(),
    })
    .array()
    .optional(),
  platforms: z
    .array(
      z.union([
        z.literal("ios"),
        z.literal("android"),
        z.literal("web"),
      ]),
    )
    .optional(),
})

type HeadlineProps = z.infer<typeof textareaSchema>

export default function HeadlineTextArea(
  props: HeadlineProps,
) {
  const [text, setText] = useState("")
  const {
    bubble_text,
    title,
    subtitle,
    button_text,
    without_background,
    className,
    title_attr,
    without_qr,
    reversed,
    platforms,
    tag,
    words,
    placeholder,
    left_icon,
    right_icon,
  } = props

  const acceptedPlatforms = platforms ?? [
    "ios",
    "android",
    "web",
  ]

  const [subscriptionPopupOpen, setSubscriptionPopupOpen] =
    useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { notify } = useContext(NotificationContext)
  const { t } = useTranslation()

  const { userInfo } = useAuth()
  const router = useRouter()

  async function handleClick() {
    setIsLoading(true)
    if (userInfo.isAnonymous) {
      router.push(
        `/login?redirect_url=/chat?initial_message=${text}`,
      )
      setIsLoading(false)
      return
    }

    try {
      const context = isStringUrl(text)

      await axios
        .post<StartSessionResponse>(
          "/api/wizard-start-session",
          {
            context,
            group: null,
            tag: null,
          },
        )
        .then((r) => {
          router.push(`/chat?session_id=${r.data.id}`)
        })
      setText("")
    } catch (e) {
      console.error(e)

      if (e instanceof AxiosError) {
        if (
          e.response?.status === 431 ||
          e.response?.status === 432
        ) {
          setSubscriptionPopupOpen(true)
        } else {
          notify(
            <ErrorMessage>
              {t("txt_something_wrong")}
            </ErrorMessage>,
          )
        }
      } else {
        notify(
          <ErrorMessage>
            {t("txt_something_wrong")}
          </ErrorMessage>,
        )
      }
    }
    setIsLoading(false)
  }

  const [inputHistory, _] = useState<string[]>([])

  useEffect(() => {
    const handleUndo = (e: KeyboardEvent) => {
      if (
        (e.ctrlKey && e.key === "z") ||
        (e.metaKey && e.key === "z")
      ) {
        if (inputHistory.length > 0) {
          setText && setText(inputHistory.pop() ?? "")
        }
      }
    }

    window.addEventListener("keydown", handleUndo)

    return () => {
      window.removeEventListener("keydown", handleUndo)
    }
  }, [text, inputHistory, setText])

  return (
    <>
      <section className="relative flex w-full items-center justify-center px-4 tablet:px-0 desktop:px-[calc(50vw-702px)]">
        {!without_background && (
          <img
            src={assetUrl("/comps/textarea-bg.webp")}
            alt="bg-picture"
            className="absolute left-0 top-0 h-[330px] w-full dark:opacity-40 tablet:h-auto tablet:max-h-[600px]"
          />
        )}
        <div
          className={clsx(
            "relative flex w-full flex-col items-center gap-6 tablet:w-[720px] tablet:items-start tablet:justify-between desktop:w-full desktop:px-[30px]",
            reversed
              ? "tablet:flex-row-reverse"
              : "tablet:flex-row",
            className
              ? className
              : "py-[35px] tablet:py-[40px] desktop:pb-[60px] desktop:pt-[100px]",
          )}>
          <div className="flex w-full flex-col items-center gap-2 tablet:w-auto tablet:max-w-[295px] tablet:items-start desktop:max-w-[600px]">
            {bubble_text && (
              <div className="z-[1] flex flex-row justify-center">
                <Bubble
                  text={bubble_text}
                  color="pinkPurpleLight"
                />
              </div>
            )}
            <GetAnimatedHeader
              tag={tag}
              title={title}
              title_attr={title_attr}
              textStart>
              {words && <AnimatedWords words={words} />}
            </GetAnimatedHeader>
            <p className="font z-[1] font-400 leading-[1.618] text-blue-700 tablet:mt-[20px] tablet:text-[16px] desktop:text-[18px]">
              {subtitle}
            </p>
          </div>
          <div className="flex w-full flex-col justify-center tablet:mt-10 tablet:w-auto desktop:mt-11">
            <div className="relative z-20 flex h-[170px] w-full rounded-[8px] border border-color-separator bg-color-cell pb-5 tablet:w-[356px] desktop:w-[546px]">
              <textarea
                disabled={isLoading}
                placeholder={placeholder}
                className={clsx(
                  "text-body-5 no-scrollbar h-full w-full resize-none rounded-[14px] bg-color-cell py-2 pl-3 pr-3 placeholder:text-color-placeholder focus-visible:outline-none",
                  isLoading
                    ? "text-color-placeholder"
                    : "text-blue-800",
                )}
                value={text}
                onChange={(e) => {
                  const inputValue = e.currentTarget.value
                  if (inputValue.length <= 1000) {
                    setText(inputValue)
                  }
                }}
              />
              <button
                disabled={isLoading}
                onClick={() => {
                  setText("")
                }}
                className={clsx(
                  "absolute bottom-2 right-3 flex h-[17px] w-[17px] items-center justify-center rounded-full",
                  "bg-blue-200 transition-all hover:bg-blue-400 disabled:opacity-40",
                  text.length === 0
                    ? "pointer-events-none opacity-0"
                    : "opacity-100",
                )}>
                <CustomThemedResource
                  format="svg"
                  source="/general/close"
                  className="h-[12px] w-[12px]"
                />
              </button>
            </div>
            <button
              onClick={(e) => {
                e.preventDefault()
                if (text.trim().length === 0) {
                  return
                }
                handleClick()
              }}
              disabled={
                isLoading || text.trim().length === 0
              }
              className={clsx(
                "z-[1] mt-7 flex w-full shrink-0 select-none flex-row items-center justify-center gap-2 text-[18px] font-600",
                "rounded-[6px] tablet:mt-4 desktop:mt-5 desktop:h-[64px] desktop:w-full desktop:rounded-[10px]",
                "bg-primary-500 text-color-white transition-colors duration-300 ease-in-out hover:bg-primary-600",
                "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
                "px-[34px] py-[14px]",
              )}>
              {left_icon && <img src={left_icon} alt="left icon" />}
              {button_text}
              {right_icon && <img src={right_icon} alt="right icon" />}
            </button>
            {!without_qr && (
              <PlatformsSection
                platforms={acceptedPlatforms}
              />
            )}
          </div>
        </div>
      </section>
      <SubscriptionPopup
        isOpen={subscriptionPopupOpen}
        close={() => setSubscriptionPopupOpen(false)}
        location={router.asPath}
      />
    </>
  )
}
